import { Box, Button, Center, Flex, HStack, Input, Spinner, Stack, Text } from '@chakra-ui/react';
import { ChangeEvent, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useDebounce } from 'use-debounce';

import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { useBulkInternalEnrolments } from '@duke-hq/app-provider-web/tenant/components/BulkEnrolments/useBulkInternalEnrolments';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import {
  CourseKindLabels,
  courseSection,
} from '@duke-hq/app-provider-web/tenant/Courses/constants';
import { SectionedList } from '@duke-hq/ui/components/SectionedList/SectionedList';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';

function CourseList({
  courses,
}: {
  courses: RouterOutputs['courses']['listWithMinimalAttributes'] | undefined;
}) {
  const { selectedCourseAttributes, setSelectedCourseAttributes } = useBulkInternalEnrolments();

  return (
    <SectionedList
      items={courses ?? []}
      getItemKey={(course) => course.id}
      getSection={(course) => courseSection(course)}
      isItemSelected={(course) => selectedCourseAttributes.id === course.id}
      onSelectItem={(course) =>
        setSelectedCourseAttributes({
          id: course.id,
          identifier: course.identifier ?? '',
          title: course.title,
          specialisation: course.specialisation ?? '',
          price_aud: Number(course.price_aud),
          kind: course.kind,
        })
      }
      emptyState={
        <Text color="fg.muted" textAlign="left" textStyle="sm">
          <Trans>No courses found.</Trans>
        </Text>
      }
      renderItem={(course) => (
        <Flex align="center" justify="space-between" w="full">
          <Box>
            <HStack justify="start">
              <Text color="fg.subtle" fontSize="sm" lineHeight="5">
                {course.identifier}
              </Text>
              {typeof course.specialisation === 'string' && course.specialisation !== '' ? (
                <Text color="fg.emphasis" fontSize="sm" lineHeight="5" fontWeight="bold">
                  {course.title}
                </Text>
              ) : null}
            </HStack>
            {typeof course.specialisation === 'string' && course.specialisation !== '' ? (
              <Text color="fg.subtle" fontSize="xs" lineHeight="5">
                {course.specialisation}
              </Text>
            ) : (
              <Text color="fg.emphasis" fontSize="sm" lineHeight="5" fontWeight="bold">
                <Box as="span">
                  {course.kind && CourseKindLabels[course.kind] ? (
                    <Box as="span">{CourseKindLabels[course.kind]}: </Box>
                  ) : null}
                  <Box as="span">{course.title}</Box>
                </Box>
              </Text>
            )}
          </Box>
        </Flex>
      )}
    />
  );
}

export function SelectSkillSet({
  goToNextStep,
  goToPrevStep,
}: {
  goToNextStep: () => void;
  goToPrevStep: () => void;
}) {
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 500);
  const { selectedCourseAttributes } = useBulkInternalEnrolments();

  const { data: coursesList, isLoading } = trpcTenant.courses.listWithMinimalAttributes.useQuery({
    search: debouncedSearch,
    fetchInternalEnrolments: true,
    order: 'category',
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const q = value.trim();
    setSearch(q);
  };

  if (!coursesList) {
    return (
      <Box w="full">
        <Text color="fg.muted" textAlign="left" textStyle="sm">
          <Trans>Sorry, we couldn't load courses. Please refresh or try again later.</Trans>
        </Text>
      </Box>
    );
  }

  return (
    <Stack spacing={4} w="full" h="full" justify="space-between">
      <FormMainContainer>
        <FormContentContainer>
          <Input
            autoComplete="off"
            autoFocus
            fontSize="sm"
            minH="8"
            maxH="8"
            onChange={handleInputChange}
            placeholder="Search"
            size="sm"
            type="text"
            value={search}
            width="full"
          />
          {isLoading ? (
            <Center h="full" w="full">
              <Spinner color="border.accent" size="lg" />
            </Center>
          ) : (
            <CourseList courses={coursesList} />
          )}
        </FormContentContainer>
        <FormFooterContainer>
          <Button
            rightIcon={<ChevronRightIcon />}
            onClick={goToNextStep}
            isDisabled={!selectedCourseAttributes.id}
          >
            <Trans>Next</Trans>
          </Button>
          <Button onClick={goToPrevStep} variant="tertiary" leftIcon={<ChevronLeftIcon />}>
            <Trans>Back</Trans>
          </Button>
        </FormFooterContainer>
      </FormMainContainer>
    </Stack>
  );
}
